import React, { useState, useEffect } from 'react';
import { ArrowDown, Download, Github, Globe, Sparkles, Code, Zap } from 'lucide-react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Button } from '../ui/button';

const Hero = ({ data }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.8]);
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const x = useSpring(0, springConfig);
  const ySpring = useSpring(0, springConfig);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPct = (clientX / innerWidth - 0.5) * 2;
      const yPct = (clientY / innerHeight - 0.5) * 2;
      x.set(xPct * 20);
      ySpring.set(yPct * 20);
      setMousePosition({ x: clientX, y: clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, ySpring]);

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const floatingIcons = [
    { icon: Code, delay: 0, x: '10%', y: '20%' },
    { icon: Sparkles, delay: 0.5, x: '80%', y: '30%' },
    { icon: Zap, delay: 1, x: '15%', y: '70%' },
    { icon: Github, delay: 1.5, x: '85%', y: '60%' },
  ];

  const companyLogos = [
    { src: '/images/polyai-logo-inverted-rgb-900px-w-72ppi-min.webp', alt: 'PolyAI', delay: 0.8, x: '70%', y: '12%', size: 'w-16 h-16' },
    { src: '/images/h2o.png', alt: 'H2O.ai', delay: 1.2, x: '8%', y: '35%', size: 'w-14 h-14' },
    { src: '/images/google.png', alt: 'Google', delay: 1.6, x: '82%', y: '65%', size: 'w-14 h-14' },
    { src: '/images/databricks.jfif', alt: 'Databricks', delay: 2.0, x: '15%', y: '75%', size: 'w-14 h-14' },
    { src: '/images/thoughtbot.png', alt: 'thoughtbot', delay: 2.4, x: '85%', y: '35%', size: 'w-13 h-13' },
    { src: '/images/clinically.jfif', alt: 'ClinicalNotes', delay: 2.8, x: '12%', y: '15%', size: 'w-12 h-12' },
    { src: '/images/constrafor.png', alt: 'Constrafor', delay: 3.2, x: '75%', y: '80%', size: 'w-14 h-14' },
    { src: '/images/rentspree.png', alt: 'RentSpree', delay: 3.6, x: '5%', y: '60%', size: 'w-12 h-12' },
    { src: '/images/arini.jfif', alt: 'Arini', delay: 4.0, x: '88%', y: '20%', size: 'w-11 h-11' },
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Modern Background Grid */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(71, 85, 105, 0.4) 1px, transparent 0)`,
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute top-32 right-32 w-64 h-64 bg-slate-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-32 left-32 w-80 h-80 bg-slate-500/3 rounded-full blur-3xl" />
        
        {/* Floating Company Logos */}
        {companyLogos.map((logo, index) => (
          <motion.div
            key={index}
            className={`absolute ${logo.size} opacity-[0.25] hover:opacity-[0.4] transition-opacity duration-500`}
            style={{
              left: logo.x,
              top: logo.y,
            }}
            initial={{ opacity: 0, scale: 0, rotate: -10 }}
            animate={{ 
              opacity: 0.25, 
              scale: 1, 
              rotate: 0,
              y: [0, -15, 0],
              x: [0, 8, 0]
            }}
            transition={{ 
              delay: logo.delay, 
              duration: 0.8,
              y: { duration: 8, repeat: Infinity, ease: "easeInOut" },
              x: { duration: 12, repeat: Infinity, ease: "easeInOut" }
            }}
            whileHover={{ scale: 1.15, opacity: 0.5 }}
          >
            <div className="w-full h-full bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-600/40 p-3 flex items-center justify-center shadow-lg">
              <img 
                src={logo.src} 
                alt={`${logo.alt} logo`}
                className="w-full h-full object-contain filter brightness-90 hover:brightness-110 hover:filter-none transition-all duration-500"
              />
            </div>
          </motion.div>
        ))}
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="max-w-5xl mx-auto"
          style={{ y, opacity, scale }}
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
            {/* Left Column - Text Content */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="space-y-6">
                <motion.div 
                  className="inline-flex items-center gap-2 bg-slate-800/30 px-4 py-2 rounded-full border border-slate-600/30"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse" />
                  <span className="text-slate-300 text-sm font-medium">Building thoughtfully</span>
                </motion.div>

                <motion.h1 
                  className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <span className="text-slate-50">Hello, I'm </span>
                  <span className="bg-gradient-to-r from-slate-200 via-slate-100 to-slate-300 bg-clip-text text-transparent">
                    {data.name}
                  </span>
                </motion.h1>

                <motion.div 
                  className="flex flex-col gap-2 text-xl lg:text-2xl"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <span className="text-slate-300 font-semibold">{data.title}</span>
                  <span className="text-slate-500">{data.subtitle}</span>
                </motion.div>

                <motion.p 
                  className="text-lg text-slate-300 leading-relaxed max-w-2xl"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {data.tagline}
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  className="flex flex-col sm:flex-row gap-4 pt-6"
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <motion.button
                    className="group inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-100 px-8 py-4 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl border border-slate-600/50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={scrollToAbout}
                  >
                    <span>Explore My Work</span>
                    <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                  </motion.button>
                  
                  {data.resumeUrl && (
                    <motion.button
                      className="inline-flex items-center gap-2 border border-slate-600 text-slate-300 hover:border-blue-500 hover:text-blue-400 px-8 py-4 rounded-xl font-medium transition-all duration-200"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => window.open(data.resumeUrl, '_blank')}
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Resume</span>
                    </motion.button>
                  )}
                </motion.div>
              </div>
            </motion.div>

            {/* Right Column - Profile Image */}
            <motion.div 
              className="hidden lg:flex items-center justify-center relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <div className="relative">
                {/* Background decorative elements */}
                <motion.div 
                  className="absolute -inset-4 bg-gradient-to-br from-slate-800/20 to-slate-700/10 rounded-3xl backdrop-blur-sm border border-slate-600/20 -z-10"
                  animate={{ rotate: [0, 1, -1, 0] }}
                  transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div 
                  className="absolute -top-6 -right-6 w-20 h-20 bg-slate-600/20 rounded-2xl backdrop-blur-sm border border-slate-500/30 -z-10"
                  animate={{ y: [-6, 6, -6], rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div 
                  className="absolute -bottom-4 -left-4 w-24 h-24 bg-slate-700/15 rounded-full backdrop-blur-sm border border-slate-600/20 -z-10"
                  animate={{ scale: [1, 1.03, 1], x: [-1, 1, -1] }}
                  transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                
                {/* Profile Image Container */}
                <motion.div
                  className="relative w-80 h-80 rounded-2xl overflow-hidden"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
                  whileHover={{ scale: 1.02 }}
                >
                  {/* Image frame with subtle border */}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-700/20 via-slate-600/15 to-slate-800/20 rounded-2xl p-[1px]">
                    <div className="w-full h-full bg-slate-900/30 rounded-2xl overflow-hidden backdrop-blur-sm">
                      <img
                        src="/DW.png"
                        alt={data.name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  
                  {/* Subtle overlay for better integration */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/10 via-transparent to-transparent rounded-2xl" />
                  
                  {/* Floating accent elements */}
                  {floatingIcons.slice(0, 4).map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <motion.div
                        key={index}
                        className="absolute w-8 h-8 bg-slate-800/70 rounded-lg border border-slate-600/50 backdrop-blur-sm flex items-center justify-center"
                        style={{
                          left: item.x,
                          top: item.y,
                        }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1 + item.delay, duration: 0.5 }}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <IconComponent className="w-4 h-4 text-blue-400" />
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>
            </motion.div>
          </div>
          
          {/* Scroll Indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 0.8 }}
          >
            <motion.button
              onClick={scrollToAbout}
              className="text-slate-400 hover:text-blue-400 transition-colors duration-300 p-2 rounded-full hover:bg-blue-400/10"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ArrowDown size={20} />
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;